// Shared pieces of the firm screens: loading firm read models with their access rule, an act button that opens the command's
// generated form (firm scope), asking the engine a read command through the inbox and showing its answer verbatim, and the
// approval panel — the plain-language explanation and the evidence come first, Approve / Reject are the signed-in partner's acts.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ActionDrawer, type ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { Button, Card, EmptyState, ErrorState, LoadingRows, StatusPill } from "../../components";
import { inboxRow } from "../../data/rows";
import { FIRM_MODEL_MIN_ROLE, mayReadFirmModel } from "../../data/source";
import type { FirmModel, FirmModelKind } from "../../data/types";
import { mayRequest } from "../../lib/commands";
import { fmtStamp, titleCase } from "../../lib/format";
import { useLoad, useRealtime, type Loaded } from "../../lib/hooks";

export type Models = Partial<Record<FirmModelKind, FirmModel | null>>;

/** Firm read models by kind (null: not published or not readable by this role). Reloads when the engine republishes them. */
export function useFirmModels(kinds: FirmModelKind[]): Loaded<Models> {
  const { ds } = useApp();
  const l = useLoad(async () => {
    const ms = await Promise.all(kinds.map((k) => ds.firmModel(k)));
    return Object.fromEntries(kinds.map((k, i) => [k, ms[i]])) as Models;
  }, [ds, kinds.join()]);
  useRealtime(ds, "actions", (e) => { if (!e.engagementId) l.reload(); });
  return l;
}

/** One firm model's document, or the reason it cannot be shown (role, not published, the engine could not compute it). */
export function ModelGate<T>({ kind, m, children, label }: { kind: FirmModelKind; m: FirmModel | null | undefined; children: (doc: T, m: FirmModel) => ReactNode; label?: string }) {
  const { me } = useApp();
  const name = label ?? titleCase(kind);
  if (!mayReadFirmModel(me.role, kind) || (m === null && FIRM_MODEL_MIN_ROLE[kind] === "partner" && me.role !== "partner"))
    return <EmptyState title={`${name}: ${titleCase(FIRM_MODEL_MIN_ROLE[kind] ?? "staff")}s only.`}>The firm's access rules keep this from your role ({me.role}).</EmptyState>;
  if (!m) return <EmptyState title={`${name} is not published yet.`}>The engine that holds the firm's sync role publishes it (cloud push-firm).</EmptyState>;
  if (m.error) return <ErrorState error={`The engine could not compute ${name.toLowerCase()}: ${m.error}`} />;
  if (m.doc === null || m.doc === undefined) return <ErrorState error={`${name} was published without a document.`} />;
  return <>{children(m.doc as T, m)}</>;
}

export function Computed({ m }: { m: FirmModel | null | undefined }) {
  return m?.computedAt ? <span className="text-xs text-muted">computed {fmtStamp(m.computedAt)}</span> : null;
}

/** Summary tiles of figures the engine counted. */
export function Tiles({ items }: { items: Array<{ label: string; value: ReactNode; tone?: "blocked" | "waiting" | "done" }> }) {
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {items.map((t) => (
        <div key={t.label} className={`rounded-md border px-3 py-2 ${t.tone === "blocked" ? "border-blocked-line bg-blocked-bg" : t.tone === "waiting" ? "border-waiting-line bg-waiting-bg" : t.tone === "done" ? "border-done-line bg-done-bg" : "border-line bg-sunken"}`}>
          <dt className="text-xs text-muted">{t.label}</dt><dd className="text-lg font-semibold tabular-nums">{t.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The firm-scope act surface of a page: `act(intent)` opens the command's form; the drawer is rendered once. */
export function useActs(): [(i: Omit<ActionIntent, "engagementId"> & { engagementId?: string | null }) => void, ReactNode] {
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  const act = useCallback((i: Omit<ActionIntent, "engagementId"> & { engagementId?: string | null }) => setIntent({ engagementId: null, ...i }), []);
  return [act, <ActionDrawer key="acts" intent={intent} onClose={() => setIntent(null)} />];
}

/** A button for one catalogue command: disabled with the catalogue's reason when this person may not ask for it. */
export function ActButton({ command, label, onClick, tone, scope = "firm", size = "sm" }: {
  command: string; label: string; onClick: () => void; tone?: "primary" | "danger"; scope?: "firm" | "engagement"; size?: "sm" | "md";
}) {
  const { catalogue, me } = useApp();
  const spec = catalogue.get(command);
  const ok = mayRequest(spec, me, scope);
  return (
    <Button size={size} kind={tone === "primary" ? "primary" : tone === "danger" ? "danger" : "secondary"} disabled={!ok.ok} onClick={onClick} data-command={command}
      title={ok.ok ? (spec?.humanOnly ? `${spec.summary} — your own act` : spec?.summary) : ok.reason ?? undefined}>
      {label}{!ok.ok && <span className="sr-only"> — {ok.reason}</span>}
    </Button>
  );
}

export type ReadState<T> = { state: "idle" | "asking" | "answered" | "refused" | "failed" | "not_sent"; result?: T; message?: string };

/** Ask the engine a read command (inbox, mode read) and wait for its answer (`inbox_results`), shown verbatim. `auto` asks once
 * on mount. A read never changes the record. */
export function useEngineRead<T = unknown>(command: string, payload: Record<string, unknown> | null, what: string, auto = true): ReadState<T> & { ask: () => void } {
  const { ds } = useApp();
  const request = useEngine();
  const [st, setSt] = useState<ReadState<T>>({ state: "idle" });
  const waiting = useRef<string | null>(null);
  const key = payload ? JSON.stringify(payload) : null;
  const settle = useCallback(async (id: string) => {
    const r = await ds.readResult(id);
    if (waiting.current === id && r) setSt({ state: "answered", result: r.result as T });
  }, [ds]);
  useEffect(() => ds.subscribe("inbox", (e) => {
    if (!e.row || String(e.row.id) !== waiting.current) return;
    const row = inboxRow(e.row);
    if (row.status === "done") void settle(row.id);
    else if (row.status === "refused" || row.status === "failed") setSt({ state: row.status, message: row.refusal ?? "No reason given." });
  }), [ds, settle]);
  const ask = useCallback(() => {
    if (!payload) return;
    setSt({ state: "asking" });
    void request({ command, engagementId: null, payload, what }).then((row) => {
      if (!row) { setSt({ state: "not_sent" }); return; }
      waiting.current = row.id;
      if (row.status === "done") void settle(row.id);
    });
  }, [command, key, request, settle, what]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { waiting.current = null; setSt({ state: "idle" }); if (auto && payload) ask(); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...st, ask };
}

/** What a read request is doing, in words; children render the answer. */
export function ReadView<T>({ r, children, label }: { r: ReadState<T> & { ask: () => void }; children: (v: T) => ReactNode; label: string }) {
  if (r.state === "answered" && r.result !== undefined) return <>{children(r.result)}</>;
  if (r.state === "refused" || r.state === "failed") return <ErrorState error={`The engine ${r.state === "refused" ? "refused" : "could not answer"}: ${r.message}`} onRetry={r.ask} />;
  if (r.state === "not_sent") return <div className="flex items-center gap-2 text-sm text-muted">Not asked. <Button size="sm" onClick={r.ask}>Ask the engine</Button></div>;
  if (r.state === "asking") return <div className="space-y-1"><p className="text-sm text-muted">Asked the engine for {label}; it answers when it next runs the inbox.</p><LoadingRows rows={2} label={`Waiting for ${label}`} /></div>;
  return <Button size="sm" onClick={r.ask}>Ask the engine for {label}</Button>;
}

/** An approval screen's body: the engine's plain-language explanation and the evidence come first; the decision buttons sit
 * below them and stay off until the explanation is on screen. Approve / Reject are partner acts in the person's own name. */
export function ApprovalPanel({ explanation, evidence, decisions, missing }: {
  explanation: string | null | undefined; evidence?: ReactNode; decisions: ReactNode; missing?: string;
}) {
  return (
    <div className="space-y-3">
      <section aria-label="Explanation">
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Explanation (the engine's words)</h4>
        {explanation ? <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-sunken p-3 font-sans text-sm">{explanation}</pre>
          : <p className="rounded-md border border-waiting-line bg-waiting-bg px-3 py-2 text-sm text-waiting">{missing ?? "No explanation yet. The decision waits for one."}</p>}
      </section>
      {evidence && <section aria-label="Evidence"><h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Evidence</h4>{evidence}</section>}
      <div role="group" aria-label="Decision" className="flex flex-wrap items-center gap-2 border-t border-line pt-3">{decisions}</div>
    </div>
  );
}

export function Section({ title, children, action, id }: { title: string; children: ReactNode; action?: ReactNode; id?: string }) {
  return <Card title={<span id={id}>{title}</span>} action={action}>{children}</Card>;
}

export const Pill = StatusPill;
